import { Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type MicrosoftIntegration } from "~/api/v2/useMicrosoftIntegration";
import { cn } from "~/lib/utils";
import { isLegacyMigrationCompleted } from "./LegacyMigrationCard";

export type StepId = "adminConsent" | "userMigration" | "speSetup";

interface Step {
  id: StepId;
  title: string;
  description: string;
  status: "pending" | "current" | "in_progress" | "completed" | "skipped";
}

interface OnboardingSidebarProps {
  data: MicrosoftIntegration;
  currentStep: StepId;
  onStepClick?: (stepId: StepId) => void;
}

function getSteps(
  data: MicrosoftIntegration,
  t: (key: string) => string,
): Step[] {
  const steps: Step[] = [];

  const adminConsentCompleted = data.state !== "pending_admin_consent";

  // Step 1: Admin Consent
  steps.push({
    id: "adminConsent",
    title: t("msIntegration.sidebar.adminConsent"),
    description: t("msIntegration.sidebar.adminConsentDesc"),
    status: adminConsentCompleted ? "completed" : "current",
  });

  // Step 2: User Migration (only if there's a previous app to migrate from)
  if (data.hasLegacyProvider) {
    const userMigrationCompleted = isLegacyMigrationCompleted();
    steps.push({
      id: "userMigration",
      title: t("msIntegration.sidebar.userMigration"),
      description: t("msIntegration.sidebar.userMigrationDesc"),
      status: userMigrationCompleted
        ? "completed"
        : adminConsentCompleted
          ? "current"
          : "pending",
    });
  }

  // Step 3: SPE Setup (Document Migration)
  const previousStepsCompleted =
    adminConsentCompleted &&
    (!data.hasLegacyProvider || isLegacyMigrationCompleted());

  steps.push({
    id: "speSetup",
    title: t("msIntegration.sidebar.speSetup"),
    description: t("msIntegration.sidebar.speSetupDesc"),
    status:
      data.state === "completed"
        ? "completed"
        : data.state === "migration_in_progress"
          ? "in_progress"
          : previousStepsCompleted
            ? "current"
            : "pending",
  });

  return steps;
}

export function getCurrentStep(data: MicrosoftIntegration): StepId {
  // Find the first non-completed step
  if (data.state === "pending_admin_consent") return "adminConsent";

  if (data.hasLegacyProvider) {
    if (!isLegacyMigrationCompleted()) return "userMigration";
  }

  return "speSetup";
}

export function OnboardingSidebar({
  data,
  currentStep,
  onStepClick,
}: OnboardingSidebarProps) {
  const { t } = useTranslation();
  const steps = getSteps(data, t);

  return (
    <nav aria-label="Progress" className="w-full">
      <ol className="space-y-4">
        {steps.map((step, index) => (
          <li key={step.id}>
            <button
              type="button"
              onClick={() => onStepClick?.(step.id)}
              disabled={step.status === "pending"}
              className={cn(
                "group flex w-full items-start gap-4 rounded-lg p-3 text-left transition-colors",
                step.id === currentStep && "bg-muted",
                step.status !== "pending" && "hover:bg-muted/50 cursor-pointer",
                step.status === "pending" && "cursor-not-allowed opacity-50",
              )}
            >
              <StepIndicator step={step} index={index} />
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    "text-sm font-medium",
                    step.status === "completed" && "text-muted-foreground",
                    step.id === currentStep && "text-foreground",
                  )}
                >
                  {step.title}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {step.description}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function StepIndicator({ step, index }: { step: Step; index: number }) {
  if (step.status === "completed") {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
        <Check className="h-4 w-4" />
      </span>
    );
  }

  if (step.status === "in_progress") {
    return (
      <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }

  if (step.status === "current") {
    return (
      <span className="bg-primary text-primary-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium">
        {index + 1}
      </span>
    );
  }

  // pending
  return (
    <span className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium">
      {index + 1}
    </span>
  );
}
